import { AuthenticationDelgate } from "./authentication.delegate";
import { ISession, orgIds, Role, INewProfilePic, IClientSpocsClient } from "./authentication.interface";
import { of as observableOf, Observable, BehaviorSubject } from "rxjs";
import { mergeMap, catchError, share, filter } from "rxjs/operators";
import * as _ from 'lodash';
import { ConfigService, IBaseRequest } from "../ConfigService";
import { StorageKeyConstant, StorageService } from "../storage";

export class AuthenticationService
{
    public session: ISession = {
        loggedin: true,
        authCallMade: false,
        roles: []
    } as unknown as ISession;
    private sessionSubject: BehaviorSubject<ISession> = new BehaviorSubject(this.session);
    public userSession: Observable<ISession> = this.sessionSubject.pipe(filter((data) => !!data), share());

    private profilePicUrlSubject: BehaviorSubject<string> = new BehaviorSubject("assets/images/user/avatar-3.jpg");
    public profilePicUrl: Observable<string> = this.profilePicUrlSubject.pipe(filter((data) => !!data), share());


    constructor(private authenticationDelgate: AuthenticationDelgate, private storageService: StorageService)
    {
    }

    public GetOrgId(): string
    {
        return this.session.myOrgId;
    }

    public GetUserId(): string
    {
        return this.session.userId;
    }

    public GetClientId(): string
    {
        return this.session.myClientId;
    }

    public GetSalesRepId(): string
    {
        return this.session.salesRepId;
    }

    public GetAccountManagerId(): string
    {
        return this.session.accountManagerId;
    }

    public GetEmployeeId(): string
    {
        return this.session.employeeId;
    }

    public setOrgId(orgID: string)
    {
        this.session.myOrgId = orgID;
    }

    public setClientId(clientId: string)
    {
        this.session.myClientId = clientId;
    }

    public isAuthenticated(): boolean
    {
        return this.session.loggedin;
    }

    public get userName(): string
    {
        return this.session.userName;
    }
    
    public get fullName(): string
    {
        return this.session.fullName;
    }

    public get emailAddress(): string
    {
        return this.session.emailAddress;
    }

    public login(userName: string, password: string): Observable<boolean>
    {

        let authResponse = this.authenticationDelgate.login(userName, password);

        authResponse.subscribe(onAuthSuccess.bind(this), onAuthFault.bind(this));

        return authResponse.pipe(mergeMap((response: any) =>
        {
            if (!response)
            {
                return observableOf(false);
            }

            return observableOf(true);

        }),
            catchError(() => observableOf(false)),
            share());

        function onAuthSuccess(response: any)
        {
            this.updateSession(response, true);
        }

        function onAuthFault(error: any)
        {
            this.session = {
                userName: userName,
                loggedin: false,
                error: error.message[0].description
            };
            this.sessionSubject.next(this.session);
        }
    }

    public updateProfilepic(profilePic: INewProfilePic): Observable<boolean>
    {

        profilePic.orgId = this.session.myOrgId;
        profilePic.userId = this.session.userId;
        let authResponse = this.authenticationDelgate.updateProfilePic(profilePic);

        authResponse.subscribe(onProflePicUpdate.bind(this), onProflePicUpdate.bind(this));

        return authResponse.pipe(mergeMap((response: any) =>
        {
            if (!response)
            {
                return observableOf(false);
            }

            return observableOf(true);

        }),
            catchError(() => observableOf(false)),
            share());

        function onProflePicUpdate(response: any)
        {
            this.session.profilePicUrl = response.data.profilePicUrl;
            this.sessionSubject.next(this.session);
            this.profilePicUrlSubject.next(this.session.profilePicUrl);
        }
    }

    public logout(): Observable<boolean>
    {
        console.log('AuthenticationService: Logout initiated');
        let authResponse = this.authenticationDelgate.logout();

        authResponse.subscribe(onLogout.bind(this), onLogout.bind(this));

        return authResponse.pipe(mergeMap((response: any) =>
        {
            console.log('AuthenticationService: Logout response received');
            return observableOf(true);
        }),
            catchError((error) => {
                console.log('AuthenticationService: Logout error, still proceeding with cleanup');
                return observableOf(true);
            }),
            share());

        function onLogout(error: any)
        {
            console.log('AuthenticationService: Performing logout cleanup');
            
            // Clear all stored data on logout
            this.storageService.clear();

            // Set session to logged out state
            this.session = {
                loggedin: false,
                authCallMade: false,
                authenticated: false,
                roles: []
            };
            
            // Notify subscribers that user is logged out
            this.sessionSubject.next(this.session);
            
            console.log('AuthenticationService: Logout cleanup complete');
        }
    }

    public resetPasswordFromProfile(obj: any): Observable<any>
    {
        let resp = this.authenticationDelgate.resetPasswordFromProfile(obj);
        return resp.pipe(mergeMap((response: any) =>
        {
            if (!response)
            {
                return observableOf(response);
            }

            return observableOf(false);

        }),
            catchError((error: any) => observableOf(error)),
            share());
    }

    public resetPassword(obj: any): Observable<any>
    {
        let resp = this.authenticationDelgate.resetPassword(obj);
        return resp.pipe(mergeMap((response: any) =>
        {
            if (!response)
            {
                return observableOf(response);
            }

            return observableOf(false);

        }),
            catchError((error: any) => observableOf(error)),
            share());
    }

    public resetPasswordReq(obj: any): Observable<any>
    {
        let resp = this.authenticationDelgate.resetPasswordReq(obj);
        return resp.pipe(mergeMap((response: any) =>
        {
            if (!response)
            {
                return observableOf(response);
            }

            return observableOf(false);

        }),
            catchError((error: any) => observableOf(error)),
            share());
    }

    public get getBaseRequest(): IBaseRequest
    {
        if (this.session.salesRepId)
        {
            return {
                userId: this.session.userId,
                userName: this.session.userName,
                salesRepId: this.session.salesRepId,
                orgId: this.session.myOrgId,
                clientTimeStamp: new Date().toISOString()
            };
        }
        return {
            userId: this.session.userId,
            userName: this.session.userName,
            orgId: this.session.myOrgId,
            clientTimeStamp: new Date().toISOString()
        };
    }

    public updateSession(response: any, authCallMade = false)
    {
        if (response)
        {
            var userDetail = response.data.userDetail;
            var orgIds = userDetail.orgIds as Array<orgIds>;
            var clients = userDetail.clients as Array<IClientSpocsClient>

            this.session = {
                authCallMade: authCallMade,
                userName: userDetail.userName,
                emailAddress: userDetail.emailAddress,
                loggedin: true,
                authenticated: true,
                token: "",
                userId: userDetail.userId,
                employeeId: userDetail.employeeId,
                orgArray: orgIds,
                clientArray: clients,
                roles: userDetail.roles,
                myOrgId: userDetail.defaultOrgId,
                myClientId: userDetail.defaultClientId,
                salesRepId: userDetail.salesRepId,
                accountManagerId: userDetail.accountManagerId,
                fullName: userDetail.fullName,
                profilePicUrl: userDetail.profilePicUrl ? userDetail.profilePicUrl : "assets/images/user/avatar-3.jpg",
                orgDetails: orgIds.find(org => org.orgId === userDetail.defaultOrgId),
                clientDeatils: clients.find(client => client.clientId === userDetail.defaultClientId)
            };

            this.storageService.setItem(StorageKeyConstant.USER_ID, this.session.userId);
            this.sessionSubject.next(this.session);
            this.profilePicUrlSubject.next(this.session.profilePicUrl);
        }
    }

    public hasRole(role: Role): boolean
    {
        return this.session.roles?.find(x => x === role) ? true : false;
    }

    public hasAuthCallMade(): boolean
    {
        return this.session.authCallMade;
    }

    public makeAllCalls(): boolean
    {
        return this.session.roles?.find(x => x === Role.Admin || x === Role.Hr || x === Role.Contracts || x === Role.Payroll) ? true : false;
    }
}
